import { compressImage } from '@/domain/termine/shared/utils/imageProcessing';
import { useState, useRef } from 'react';

interface UseFileUploadOptions {
  onFileSelect?: (file: File | null) => void;
  initialFile?: File | null;
}

export function useFileUpload({
  onFileSelect,
  initialFile = null,
}: UseFileUploadOptions = {}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    initialFile ? URL.createObjectURL(initialFile) : null,
  );
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFile = async (selectedFile: File) => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    const compressedFile = await compressImage(selectedFile);
    setPreviewUrl(URL.createObjectURL(compressedFile));
    onFileSelect?.(selectedFile);

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) await processFile(droppedFile);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      await processFile(e.target.files[0]);
    }
  };

  const handleRemove = (e?: React.MouseEvent) => {
    e?.stopPropagation();

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onFileSelect?.(null);
  };

  return {
    previewUrl,
    isDragOver,
    fileInputRef,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleClick,
    handleInputChange,
    handleRemove,
  };
}
